import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaWhatsapp, FaChevronDown, FaBed, FaFileContract } from "react-icons/fa";
import { TbMapPin, TbHome, TbBath, TbUsers, TbRuler, TbCalendar, TbDoor, TbBuildingCommunity } from "react-icons/tb";
import { FaEnvira } from "react-icons/fa6";
import { RiGovernmentLine, RiHandCoinLine } from "react-icons/ri";
import ImgSkeleton from "../../../Assets/pngs/img_skeleton.png";
import { MdEuro, MdOutlineVerified, MdClose } from "react-icons/md";
import { BsCalendarCheck } from "react-icons/bs";

/* ─── Booking Request Modal ─────────────────────────────────────────── */
const BookingModal = ({ property, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const overlayRef = useRef(null);

  // Close on backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  // Close on Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // Lock body scroll while modal open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const validate = () => {
    const errs = {};
    if (!form.fullName.trim()) errs.fullName = "Name is required";
    if (!form.email.trim()) {
      errs.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = "Enter a valid email";
    }
    if (!form.phone.trim()) {
      errs.phone = "Phone number is required";
    } else if (!/^\+?[\d\s\-()]{7,15}$/.test(form.phone)) {
      errs.phone = "Enter a valid phone number";
    }
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    onSubmit(form);
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleBackdropClick}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm"
      style={{ animation: "fadeIn 0.18s ease" }}
    >
      <div
        className="relative w-full sm:max-w-md bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl"
        style={{ animation: "slideUp 0.22s cubic-bezier(0.22,1,0.36,1)" }}
      >
        {/* Drag handle (mobile) */}
        <div className="flex justify-center pt-3 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-stone-200" />
        </div>

        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-stone-100">
          <div>
            <h2 className="text-base font-semibold tracking-tight text-stone-800">Your Details</h2>
            <p className="text-xs text-stone-400 mt-0.5">We'll pass this along with your booking request</p>
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 flex items-center justify-center rounded-full text-stone-400 hover:bg-stone-100 hover:text-stone-700 transition"
          >
            <MdClose size={18} />
          </button>
        </div>

        {/* Property mini-preview */}
        <div className="mx-5 mt-4 flex items-center gap-3 bg-stone-50 rounded-xl px-3 py-2.5 border border-stone-100">
          <div className="h-10 w-10 rounded-lg overflow-hidden flex-shrink-0 bg-stone-200">
            <img
              src={property.cover_image || ImgSkeleton}
              alt={property.title}
              onError={(e) => { e.target.onerror = null; e.target.src = ImgSkeleton; }}
              className="h-full w-full object-cover"
            />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-stone-700 truncate">{property.title}</p>
            <p className="text-xs text-stone-400 truncate">€{property.rent_per_month} / month</p>
          </div>
        </div>

        {/* Form Fields */}
        <div className="px-5 pt-4 pb-2 space-y-3">

          {/* Full Name */}
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">
              Full Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="fullName"
              value={form.fullName}
              onChange={handleChange}
              placeholder="e.g. Marie Dupont"
              className={`w-full px-3 py-2.5 rounded-xl border text-sm bg-white transition outline-none
                ${errors.fullName
                  ? "border-red-300 focus:border-red-400 focus:ring-2 focus:ring-red-100"
                  : "border-stone-200 focus:border-stone-400 focus:ring-2 focus:ring-stone-100"
                }`}
            />
            {errors.fullName && (
              <p className="text-xs text-red-500 mt-1">{errors.fullName}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">
              Email <span className="text-red-400">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="you@example.com"
              className={`w-full px-3 py-2.5 rounded-xl border text-sm bg-white transition outline-none
                ${errors.email
                  ? "border-red-300 focus:border-red-400 focus:ring-2 focus:ring-red-100"
                  : "border-stone-200 focus:border-stone-400 focus:ring-2 focus:ring-stone-100"
                }`}
            />
            {errors.email && (
              <p className="text-xs text-red-500 mt-1">{errors.email}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">
              Phone Number <span className="text-red-400">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="+33 6 12 34 56 78"
              className={`w-full px-3 py-2.5 rounded-xl border text-sm bg-white transition outline-none
                ${errors.phone
                  ? "border-red-300 focus:border-red-400 focus:ring-2 focus:ring-red-100"
                  : "border-stone-200 focus:border-stone-400 focus:ring-2 focus:ring-stone-100"
                }`}
            />
            {errors.phone && (
              <p className="text-xs text-red-500 mt-1">{errors.phone}</p>
            )}
          </div>

          {/* Message */}
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">
              Message <span className="text-stone-300 font-normal">(optional)</span>
            </label>
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              placeholder="Any questions or things the landlord should know…"
              rows={3}
              className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm bg-white transition outline-none focus:border-stone-400 focus:ring-2 focus:ring-stone-100 resize-none"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="px-5 pb-6 pt-2 space-y-2">
          <button
            onClick={handleSubmit}
            className="w-full bg-green-500 hover:bg-green-600 active:scale-[0.98] text-white py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 text-sm shadow-sm shadow-green-200"
          >
            <FaWhatsapp size={18} />
            Send via WhatsApp
          </button>
          <button
            onClick={onClose}
            className="w-full text-stone-500 py-2 text-sm hover:text-stone-700 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { transform: translateY(24px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
      `}</style>
    </div>
  );
};

/* ─── Main BookingRequest Page ───────────────────────────────────────── */
const BookingRequest = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const property = state?.property;

  const [detailsOpen, setDetailsOpen] = useState(() => window.innerWidth >= 768);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const handleResize = () => setDetailsOpen(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!property) return (
    <p className="text-center mt-32 text-red-500">Property not found.</p>
  );

  const propertyAddress = [property.address, property.city, property.region, property.country]
    .filter(Boolean)
    .join(", ");

  const handleWhatsAppRequest = (userDetails) => {
    const message = [
      `*🏠 Booking Request*`,
      ``,
      `*${property.title}*`,
      `📍 ${propertyAddress}`,
      `Type: ${property.property_type?.replace(/_/g, " ")}`,
      ``,
      `*👤 Requester Details*`,
      `Name: ${userDetails.fullName}`,
      `Email: ${userDetails.email}`,
      `Phone: ${userDetails.phone}`,
      ...(userDetails.message ? [`Message: ${userDetails.message}`] : []),
      ``,
      `*💰 Rent & Financials*`,
      `Rent: €${property.rent_per_month} / month`,
      `Charges: ${property.charges ? `€${property.charges}` : "Not included"}`,
      `Deposit: €${property.security_deposit}`,
      `CAF Eligible: ${property.is_caf_eligible ? "Yes" : "No"}`,
      ``,
      `*📅 Availability*`,
      `Available From: ${property.available_from}`,
      `Minimum Stay: ${property.minimum_stay_months} month${property.minimum_stay_months !== 1 ? "s" : ""}`,
      `Domicile Allowed: ${property.is_domicile_allowed ? "Yes" : "No"}`,
      ``,
      `*🏗️ Property Details*`,
      `Rooms: ${property.rooms} (${property.bhk} BHK)`,
      `Bathrooms: ${property.bathrooms}`,
      `Max Occupancy: ${property.max_people} people`,
      `Size: ${property.size_m2} m²`,
      `Furnished: ${property.furnished ? "Yes" : "No"}`,
      `Contract Provided: ${property.contract ? "Yes" : "No"}`,
      ...(property.amenities_data?.length > 0 ? [
        ``,
        `*✨ Facilities*`,
        ...property.amenities_data.map((a) => `- ${a.amenity_name}`),
      ] : []),
      ``,
      `I am interested in booking this property. Please provide the next steps for a viewing or reservation.`,
    ].join("\n");

    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/917994317698?text=${encoded}`, "_blank");
    setShowModal(false);
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">

        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-stone-200 transition"
          >
            <FaArrowLeft size={15} />
          </button>
          <h1 className="text-xl font-semibold tracking-tight">Request Booking</h1>
        </div>

        {/* Property Card */}
        <div className="rounded-2xl overflow-hidden border border-stone-200 bg-white shadow-sm">

          {/* Cover image */}
          <div className="relative h-52 bg-stone-200 overflow-hidden">
            <img
              src={property.cover_image || ImgSkeleton}
              alt={property.title}
              onError={(e) => { e.target.onerror = null; e.target.src = ImgSkeleton; }}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-3 left-4 right-4">
              <p className="text-white font-semibold text-lg leading-tight">{property.title}</p>
              <p className="text-white/80 text-xs flex items-center gap-1 mt-0.5">
                <TbMapPin size={12} /> {propertyAddress}
              </p>
            </div>
          </div>

          {/* Price */}
          <div className="px-4 py-3 border-b border-stone-100">
            <span className="text-2xl font-bold">€{property.rent_per_month}</span>
            <span className="text-sm text-stone-400"> / month</span>
          </div>

          {/* Toggle button */}
          <button
            onClick={() => setDetailsOpen((prev) => !prev)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-stone-600 hover:bg-stone-50 transition-colors"
          >
            <span>Show Property Details</span>
            <FaChevronDown
              size={13}
              className={`transition-transform duration-300 ${detailsOpen ? "rotate-180" : "rotate-0"}`}
            />
          </button>

          {/* Collapsible content */}
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${detailsOpen ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0"}`}
          >
            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-2 px-4 pb-4">
              {[
                { icon: <MdEuro size={20} />, label: "Charges", value: property.charges ? `€ ${property.charges}` : "—" },
                { icon: <RiHandCoinLine size={20} />, label: "Deposit", value: property.security_deposit ? `€ ${property.security_deposit}` : "—" },
                { icon: <TbDoor size={20} />, label: "Rooms", value: property.rooms },
                { icon: <TbBath size={20} />, label: "Bathrooms", value: property.bathrooms },
                { icon: <FaBed size={20} />, label: "BHK", value: property.bhk },
                { icon: <TbUsers size={20} />, label: "Max People", value: property.max_people },
                { icon: <TbRuler size={20} />, label: "Size", value: `${property.size_m2} m²` },
                { icon: <TbHome size={20} />, label: "Furnished", value: property.furnished ? "Yes" : "No" },
                { icon: <TbBuildingCommunity size={20} />, label: "Type", value: property.property_type?.replace("_", " ") },
                { icon: <TbCalendar size={20} />, label: "Min Stay", value: `${property.minimum_stay_months} months` },
                { icon: <BsCalendarCheck size={20} />, label: "Available From", value: property.available_from },
                { icon: <MdOutlineVerified size={20} />, label: "CAF Eligible", value: property.is_caf_eligible ? "Yes" : "No" },
                { icon: <RiGovernmentLine size={20} />, label: "Domicile", value: property.is_domicile_allowed ? "Yes" : "No" },
                { icon: <FaFileContract size={20} />, label: "Contract", value: property.contract ? "Yes" : "No" },
              ].map(({ icon, label, value }) => (
                <div key={label} className="flex gap-2 items-center bg-stone-50 p-2 rounded-xl text-xs">
                  <span className="text-stone-400">{icon}</span>
                  <div>
                    <p className="text-stone-400">{label}</p>
                    <p className="font-medium">{value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Availability info */}
            <div className="mx-4 mb-4 bg-blue-50 border border-blue-100 rounded-2xl px-4 py-3 text-xs text-blue-800 space-y-1">
              <p>• Available from: <strong>{property.available_from}</strong></p>
              <p>• Minimum stay: <strong>{property.minimum_stay_months} month{property.minimum_stay_months !== 1 ? "s" : ""}</strong></p>
            </div>

            {/* Amenities */}
            {property.amenities_data?.length > 0 && (
              <div className="mx-4 mb-4 border-t border-stone-100 pt-4">
                <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-3">Facilities</p>
                <div className="flex flex-wrap gap-2">
                  {property.amenities_data.map((a) => (
                    <span key={a.amenity_id} className="px-3 py-1 border border-stone-200 rounded-full text-xs text-stone-600">
                      {a.amenity_name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3 pb-8">
          <button
            onClick={() => setShowModal(true)}
            className="w-full bg-green-500 hover:bg-green-600 active:scale-[0.98] text-white py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 shadow-sm shadow-green-200"
          >
            <FaWhatsapp size={20} />
            Request via WhatsApp
          </button>
          <button
            onClick={() => navigate(-1)}
            className="w-full border border-stone-300 text-stone-700 py-3 rounded-xl font-medium hover:bg-stone-100 transition-colors text-sm"
          >
            Back to Listing
          </button>
        </div>

      </main>

      {/* Booking Modal */}
      {showModal && (
        <BookingModal
          property={property}
          onClose={() => setShowModal(false)}
          onSubmit={handleWhatsAppRequest}
        />
      )}
    </div>
  );
};

export default BookingRequest;